import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import AuthContext from "../../context/AuthContext";
import { Box, Button, Chip, IconButton, Typography, useTheme } from "@mui/material";
import GridMediaCard from "../../components/cards/GridMediaCard";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import PageContent from "../../components/PageContent";
import PageHero from "../../components/motion/PageHero";
import ScrollReveal from "../../components/motion/ScrollReveal";
import Fetch from "../../services/Fetch";
import { useNavigate, useParams } from "react-router-dom";
import SnackbarAlert from "../../components/SnackBar";
import useSnackBar from "../../hooks/UseSnackBar";
import { useWaits } from "../../hooks/UseWait";
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import ArrowUpwardOutlinedIcon from "@mui/icons-material/ArrowUpwardOutlined";
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined";
import { useConstants } from "../../hooks/UseConstants";
import { resolveCourseImage } from "../../helper/resolveMediaUrl";
import { formatCurrency } from "../../helper/formatCurrency";

function PathDetails() {
     const { host, language } = useConstants();
     const isAr = language === 'ar';
     const theme = useTheme();
     const { wait } = useContext(AuthContext);
     const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
     const { getWait, setGetWait } = useWaits();
     const [path, setPath] = useState(null);
     const [movingCourseId, setMovingCourseId] = useState("");
     const navigate = useNavigate();
     const param = useParams();

     const getPath = useCallback(async () => {
          const result = await Fetch(`${host}/paths/${param.id}/show`);

          if (result.status === 200) {
               setPath(result.data.data);
          }

          setGetWait(false);
     }, [host, param.id, setGetWait]);

     useEffect(() => {
          getPath();
     }, [getPath]);

     const courses = useMemo(
          () => [...(path?.courses || [])].sort((a, b) => ((a.pivot?.order ?? 0) - (b.pivot?.order ?? 0))),
          [path]
     );

     const reorderPathCourses = async (courseId, direction) => {
          if (!courses.length) return;

          const currentIndex = courses.findIndex((course) => course.id === courseId);
          const targetIndex = currentIndex + direction;

          if (currentIndex === -1 || targetIndex < 0 || targetIndex >= courses.length) {
               return;
          }

          const reordered = [...courses];
          [reordered[currentIndex], reordered[targetIndex]] = [reordered[targetIndex], reordered[currentIndex]];

          const normalized = reordered.map((course, index) => ({
               ...course,
               pivot: course.pivot ? { ...course.pivot, order: index + 1 } : course.pivot,
          }));
          const previousCourses = path.courses;

          setMovingCourseId(String(courseId));
          setPath((prev) => ({ ...prev, courses: normalized }));

          const result = await Fetch(`${host}/teacher/paths/${param.id}/reorder-courses`, 'POST', {
               course_ids: normalized.map((course) => course.id),
          });

          if (result.status !== 200) {
               setPath((prev) => ({ ...prev, courses: previousCourses }));
               setSnackBar('error', isAr ? 'تعذر حفظ ترتيب دورات المسار' : 'Could not save path course order');
          } else {
               setSnackBar('success', isAr ? 'تم تحديث ترتيب دورات المسار' : 'Path course order updated');
          }

          setMovingCourseId("");
     };

     const priceBlock = (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
               <Chip
                    size="small"
                    label={formatCurrency(path?.effective_price ?? path?.price ?? 0, isAr)}
                    sx={{ bgcolor: '#eff6ff', color: '#1d4ed8', fontWeight: 700 }}
               />
               {path?.has_active_discount ? (
                    <Typography variant="caption" color="text.secondary" sx={{ textDecoration: 'line-through' }}>
                         {formatCurrency(path?.price, isAr)}
                    </Typography>
               ) : null}
          </Box>
     );

     return (
          <>
               <PageContent wait={wait} loading={getWait}>
                    {path ? (
                         <>
                              <PageHero
                                   title={isAr ? (path.name_ar || path.name_en) : (path.name_en || path.name_ar)}
                                   subtitle={isAr ? 'عرض دورات المسار وإدارتها' : 'View and manage courses in this path'}
                                   icon={<AccountTreeOutlinedIcon />}
                              />

                              <ScrollReveal direction="down">
                                   <Box
                                        sx={{
                                             display: "flex",
                                             flexDirection: { xs: "column", md: "row" },
                                             gap: 3,
                                             mb: 3,
                                             p: 3,
                                             borderRadius: 4,
                                             bgcolor: theme.palette.background.paper,
                                             border: "1px solid",
                                             borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                                             boxShadow: "0 4px 24px rgba(13,53,140,0.08)",
                                        }}
                                   >
                                        <Box
                                             sx={{
                                                  width: { xs: "100%", md: 320 },
                                                  height: 200,
                                                  borderRadius: 3,
                                                  overflow: "hidden",
                                                  bgcolor: "#0D358C",
                                                  flexShrink: 0,
                                             }}
                                        >
                                             {path.image ? (
                                                  <Box component="img" src={resolveCourseImage(path)} alt="" sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                             ) : (
                                                  <Box sx={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(135deg, #0D358C 0%, #2563EA 100%)" }}>
                                                       <AccountTreeOutlinedIcon sx={{ fontSize: 64, color: "rgba(255,255,255,0.85)" }} />
                                                  </Box>
                                             )}
                                        </Box>

                                        <Box sx={{ flex: 1, minWidth: 0 }}>
                                             <Typography variant="h5" fontWeight={800} sx={{ color: "#0D358C", mb: 1 }}>
                                                  {isAr ? (path.name_ar || path.name_en) : (path.name_en || path.name_ar)}
                                             </Typography>
                                             <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                                                  {path.category ? (
                                                       <Chip size="small" label={isAr ? path.category.name_ar : path.category.name_en} sx={{ bgcolor: '#f8fafc' }} />
                                                  ) : null}
                                                  <Chip size="small" label={`${courses.length} ${isAr ? 'دورة' : 'courses'}`} sx={{ bgcolor: '#ecfeff', color: '#0f766e' }} />
                                             </Box>
                                             {priceBlock}
                                             <Typography variant="subtitle2" fontWeight={700} sx={{ color: "#0D358C", mt: 2, mb: 0.5 }}>
                                                  {isAr ? 'وصف المسار' : 'Path description'}
                                             </Typography>
                                             <Typography variant="body1" sx={{ color: "text.secondary", lineHeight: 1.8, whiteSpace: "pre-wrap", mb: 2 }}>
                                                  {(isAr ? path.description_ar : path.description_en) || (isAr ? 'لا يوجد وصف للمسار بعد.' : 'No path description yet.')}
                                             </Typography>
                                             <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                                  <Button variant="contained" onClick={() => navigate(`/update-path/${path.id}`)}>
                                                       {isAr ? 'تعديل المسار' : 'Update path'}
                                                  </Button>
                                                  <Button variant="outlined" onClick={() => navigate('/my-paths')}>
                                                       {isAr ? 'العودة للمسارات' : 'Back to paths'}
                                                  </Button>
                                             </Box>
                                        </Box>
                                   </Box>
                              </ScrollReveal>

                              <DashboardPanelCard
                                   title={isAr ? 'دورات المسار' : 'Path courses'}
                                   subtitle={isAr ? 'الدورات المرتبطة بهذا المسار' : 'Courses linked to this learning path'}
                                   className="mx-0"
                              >
                                   {courses.length === 0 ? (
                                        <Box sx={{ textAlign: 'center', py: 6 }}>
                                             <MenuBookOutlinedIcon sx={{ fontSize: 56, color: '#94a3b8', mb: 1 }} />
                                             <Typography color="text.secondary">
                                                  {isAr ? 'لا توجد دورات مرتبطة بهذا المسار بعد.' : 'No courses linked to this path yet.'}
                                             </Typography>
                                        </Box>
                                   ) : (
                                        <Box className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                                             {courses.map((course) => (
                                                  <GridMediaCard
                                                       key={course.id}
                                                       image={course.image}
                                                       title={isAr ? course.name_ar : course.name_en}
                                                       description={isAr ? course.description_ar : course.description_en}
                                                       footer={
                                                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                                                                 <Typography variant="caption" className="text-blue-600 font-bold">
                                                                      {formatCurrency(course.effective_price ?? course.price, isAr)}
                                                                 </Typography>
                                                                 <Typography variant="caption" color="text.secondary" fontWeight={700}>
                                                                      {isAr ? `الترتيب ${course.pivot?.order ?? "—"}` : `Order ${course.pivot?.order ?? "—"}`}
                                                                 </Typography>
                                                            </Box>
                                                       }
                                                       actions={
                                                            <>
                                                                 <IconButton
                                                                      size="small"
                                                                      onClick={() => reorderPathCourses(course.id, -1)}
                                                                      disabled={courses[0]?.id === course.id || movingCourseId === String(course.id)}
                                                                 >
                                                                      <ArrowUpwardOutlinedIcon fontSize="small" />
                                                                 </IconButton>
                                                                 <IconButton
                                                                      size="small"
                                                                      onClick={() => reorderPathCourses(course.id, 1)}
                                                                      disabled={courses[courses.length - 1]?.id === course.id || movingCourseId === String(course.id)}
                                                                 >
                                                                      <ArrowDownwardOutlinedIcon fontSize="small" />
                                                                 </IconButton>
                                                                 <Button onClick={() => navigate(`/course-details/${course.id}`)} size="small">
                                                                      {isAr ? 'عرض' : 'View'}
                                                                 </Button>
                                                                 <Button onClick={() => navigate(`/update-course/${course.id}`)} size="small">
                                                                      {isAr ? 'تعديل' : 'Update'}
                                                                 </Button>
                                                            </>
                                                       }
                                                  />
                                             ))}
                                        </Box>
                                   )}
                              </DashboardPanelCard>
                         </>
                    ) : (
                         !getWait && (
                              <Typography sx={{ textAlign: "center", py: 8, color: "text.secondary" }}>
                                   {isAr ? "تعذر تحميل المسار" : "Could not load path"}
                              </Typography>
                         )
                    )}
               </PageContent>
               <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
          </>
     );
}

export default PathDetails;